import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { useBox } from "./BoxContext";
import { X, AlertCircle } from "lucide-react";

/**
 * Top-of-screen banner that appears once per day when there are expenses
 * due within ≤1 day or overdue. Clicking it navigates to /expenses and dismisses
 * for the rest of the day. Re-appears the next day.
 */
const STORAGE_PREFIX = "palistore-expenses-banner-";

function todayKey() {
  const d = new Date();
  return `${STORAGE_PREFIX}${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

export default function ExpensesDailyAlert({ user }) {
  const { selectedBox } = useBox();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Already dismissed today?
    if (localStorage.getItem(todayKey())) return;

    const fetchExpenses = async () => {
      try {
        const url = selectedBox ? `/expenses?box_id=${selectedBox.id}&status=pendiente` : `/expenses?status=pendiente`;
        const resp = await axiosInstance.get(url);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const eligible = (resp.data || []).filter((e) => {
          const due = new Date(e.due_date);
          due.setHours(0, 0, 0, 0);
          const diffDays = Math.floor((due - today) / (1000 * 60 * 60 * 24));
          return diffDays <= 1;
        });
        if (eligible.length > 0) {
          setCount(eligible.length);
          setShow(true);
        }
      } catch (_e) {
        // silent
      }
    };
    fetchExpenses();
  }, [user, selectedBox]);

  const dismiss = () => {
    localStorage.setItem(todayKey(), "1");
    setShow(false);
  };

  const goToExpenses = () => {
    localStorage.setItem(todayKey(), "1");
    setShow(false);
    navigate("/expenses");
  };
  useEffect(() => {
    if (!show || window.innerWidth >= 768) return;
    localStorage.setItem(todayKey(), "1");
    const t = setTimeout(() => setShow(false), 6000);
    return () => clearTimeout(t);
  }, [show]);

  if (!show) return null;

  return (
    <div
      data-testid="expenses-daily-alert"
      className="fixed top-[72px] md:top-3 left-3 right-3 md:left-1/2 md:-translate-x-1/2 md:max-w-md z-[9100] rounded-xl bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-2xl border border-white/30"
    >
      <div className="p-3 flex items-center gap-3">
        <div className="flex-shrink-0 bg-white/20 rounded-lg p-2 animate-pulse">
          <AlertCircle className="w-5 h-5" />
        </div>
        <button
          onClick={goToExpenses}
          className="flex-1 min-w-0 text-left"
          data-testid="expenses-daily-alert-cta"
        >
          <p className="font-semibold text-sm">
            {count === 1 ? "Tienes 1 gasto por revisar" : `Tienes ${count} gastos por revisar`}
          </p>
          <p className="text-xs text-white/90 mt-0.5">Toca para revisar y enviar por WhatsApp.</p>
        </button>
        <button
          onClick={dismiss}
          className="text-white/80 hover:text-white p-1 -mt-1 -mr-1"
          aria-label="Cerrar"
          data-testid="expenses-daily-alert-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
